import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Upload, FileText, Download, Loader2, Eye, Pencil, Trash2, Columns2, Rows, Users, Store, Building2, Truck, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OnboardingBulkUploadDialog({ open, onClose, onComplete }) {
  const fileInputRef = React.useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/onboarding-bulk-template.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'onboarding_bulk_upload_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a file first'); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding-bulk-upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      const ok = res.data.success_count || 0;
      const dup = res.data.duplicate_count || 0;
      const err = res.data.error_count || 0;
      if (ok > 0) {
        toast.success(`Imported ${ok} record${ok === 1 ? '' : 's'} (${dup} duplicates skipped, ${err} errors)`);
      } else if (dup > 0 || err > 0) {
        toast.warning(`Nothing imported — ${dup} duplicates, ${err} errors`);
      }
      if (onComplete) onComplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <Upload size={18} /> Onboarding — Bulk Upload
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-600 bg-slate-50 border rounded p-3">
            Upload a CSV / Excel with the columns shown in the template. Required: Name · Contact Person · Email · Phone · Mobile · Address · State · District · Pincode · Business Segment.
            Invalid rows are <strong>skipped with a reason</strong> — the file will not be rejected. Existing emails / GSTINs are skipped as duplicates. Each successful row is auto-assigned the next Franchise ID.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-1"
              data-testid="onboarding-bulk-template-btn"
            >
              <FileSpreadsheet size={14} /> Download Template
            </Button>
            <CsvDownloadButton onDownload={handleDownloadTemplate} testId="onboarding-bulk-template-csv" label="Template CSV" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }}
              className="text-sm flex-1 min-w-0"
              data-testid="onboarding-bulk-file-input"
            />
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
              data-testid="onboarding-bulk-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload
            </Button>
          </div>

          {result && (
            <div className="space-y-3" data-testid="onboarding-bulk-result">
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-emerald-700" data-testid="result-success-count">{result.success_count || 0}</div>
                  <div className="text-[10px] text-emerald-700 uppercase">Imported</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-amber-700">{result.duplicate_count || 0}</div>
                  <div className="text-[10px] text-amber-700 uppercase">Duplicates</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-red-700">{result.error_count || 0}</div>
                  <div className="text-[10px] text-red-700 uppercase">Errors</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                  <div className="text-lg font-bold text-slate-700">{result.total_rows || 0}</div>
                  <div className="text-[10px] text-slate-700 uppercase">Total</div>
                </div>
              </div>
              {result.created_franchise_ids && result.created_franchise_ids.length > 0 && (
                <div className="border rounded p-2 text-xs">
                  <div className="font-semibold text-emerald-700 flex items-center gap-1 mb-1">
                    <CheckCircle2 size={12} /> Created Franchise IDs
                  </div>
                  <div className="font-mono text-slate-700 break-all">
                    {result.created_franchise_ids.join(', ')}
                  </div>
                </div>
              )}
              {[...(result.errors || []), ...(result.duplicates || [])].length > 0 && (
                <div className="border rounded">
                  <div className="px-3 py-2 bg-amber-50 border-b text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <AlertTriangle size={14} /> Skipped Rows ({(result.errors || []).length + (result.duplicates || []).length})
                  </div>
                  <div className="max-h-48 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white border-b">
                        <tr>
                          <th className="text-left px-2 py-1 font-semibold">Row</th>
                          <th className="text-left px-2 py-1 font-semibold">Name</th>
                          <th className="text-left px-2 py-1 font-semibold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(result.errors || []).map((r) => (
                          <tr key={`e-${r.row}`} data-testid={`bulk-error-row-${r.row}`}>
                            <td className="px-2 py-1 text-slate-500">{r.row}</td>
                            <td className="px-2 py-1 text-slate-700">{r.name || '—'}</td>
                            <td className="px-2 py-1 text-red-600">{r.reason}</td>
                          </tr>
                        ))}
                        {(result.duplicates || []).map((r) => (
                          <tr key={`d-${r.row}`} data-testid={`bulk-duplicate-row-${r.row}`}>
                            <td className="px-2 py-1 text-slate-500">{r.row}</td>
                            <td className="px-2 py-1 text-slate-700">{r.name || '—'}</td>
                            <td className="px-2 py-1 text-amber-700">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="onboarding-bulk-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Onboarding = () => {
  const { canCreate: canCreateOnboarding, canEdit: canEditOnboarding, canDelete: canDeleteOnboarding } = useModulePermissions();
  const [onboardingList, setOnboardingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [editingId, setEditingId] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [formLayout, setFormLayout] = useState('horizontal'); // 'horizontal' (2-col) | 'vertical' (1-col)
  const [segmentFilter, setSegmentFilter] = useState(null);   // null = show all
  
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    state: '',
    city: '',
    district: '',
    pincode: '',
    gstin: '',
    gst_document: '',
    aadhaar_document: '',
    store_image: '',
    agreement_document: '',
    business_segment: 'Regular Customer',
    project_cost: '',
    total_store_size: '',
  });

  const businessSegments = [
    'Franchise - FIFO',
    'Franchise - FICO',
    'Regular Customer',
    'Retailer- B2B',
    'Distributor',
    'C&F',
    'Dark Store'
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Puducherry'
  ];

  useEffect(() => {
    fetchData();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {}
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onboarding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOnboardingList(response.data);
    } catch (error) {
      toast.error('Failed to fetch onboarding records');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      
      setFormData({ ...formData, [fieldName]: response.data.url });
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Document upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (dialogMode === 'view') { setDialogOpen(false); return; }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        project_cost: formData.project_cost ? parseFloat(formData.project_cost) : null,
        total_store_size: formData.total_store_size ? parseFloat(formData.total_store_size) : null,
      };
      if (dialogMode === 'edit' && editingId) {
        await axios.put(`${API}/onboarding/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Onboarding record updated');
      } else {
        await axios.post(`${API}/onboarding`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Onboarding record created successfully');
      }
      setDialogOpen(false);
      setEditingId(null);
      setDialogMode('create');
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingId(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openViewDialog = (record) => {
    hydrateFormFromRecord(record);
    setEditingId(record.id);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    hydrateFormFromRecord(record);
    setEditingId(record.id);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const hydrateFormFromRecord = (record) => {
    setFormData({
      name: record.name || '',
      contact_person: record.contact_person || '',
      email: record.email || '',
      phone: record.phone || '',
      mobile: record.mobile || '',
      address: record.address || '',
      state: record.state || '',
      city: record.city || '',
      district: record.district || '',
      pincode: record.pincode || '',
      gstin: record.gstin || '',
      gst_document: record.gst_document || '',
      aadhaar_document: record.aadhaar_document || '',
      store_image: record.store_image || '',
      agreement_document: record.agreement_document || '',
      business_segment: record.business_segment || 'Regular Customer',
      project_cost: record.project_cost ?? '',
      total_store_size: record.total_store_size ?? '',
    });
  };

  const confirmDelete = async () => {
    if (!deletingRecord) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/onboarding/${deletingRecord.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Deleted ${deletingRecord.name}`);
      setDeletingRecord(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      state: '',
      city: '',
      district: '',
      pincode: '',
      gstin: '',
      gst_document: '',
      aadhaar_document: '',
      store_image: '',
      agreement_document: '',
      business_segment: 'Regular Customer',
      project_cost: '',
      total_store_size: '',
    });
  };

  const filteredOnboarding = onboardingList.filter((o) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      o.name.toLowerCase().includes(q) ||
      o.email.toLowerCase().includes(q) ||
      o.mobile.includes(searchTerm);
    const matchesSegment = !segmentFilter || o.business_segment === segmentFilter;
    return matchesSearch && matchesSegment;
  });

  // Per-segment counts (always based on the unfiltered list — cards should
  // show true totals regardless of search / segment filter).
  const segmentCounts = onboardingList.reduce((acc, o) => {
    const seg = o.business_segment || 'Unknown';
    acc[seg] = (acc[seg] || 0) + 1;
    return acc;
  }, {});

  const SEGMENT_CARDS = [
    { label: 'Franchise - FICO', key: 'Franchise - FICO', icon: Store, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Franchise - FIFO', key: 'Franchise - FIFO', icon: Store, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Regular Customer', key: 'Regular Customer', icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Retailer- B2B', key: 'Retailer- B2B', icon: Building2, color: 'text-teal-600', bg: 'bg-teal-50' },
    { label: 'Distributor', key: 'Distributor', icon: Truck, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'C&F', key: 'C&F', icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Dark Store', key: 'Dark Store', icon: Store, color: 'text-slate-700', bg: 'bg-slate-100' },
  ];

  const canExport = ['admin', 'manager'].includes(userRole);

  const handleExport = async (exportSelected = false) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (exportSelected && selectedRecords.length > 0) {
        payload.onboarding_ids = selectedRecords;
      } else if (searchTerm) {
        payload.search = searchTerm;
      }
      
      const response = await axios.post(`${API}/export/onboarding`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Onboarding_Export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportSelected ? selectedRecords.length : filteredOnboarding.length} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) ? prev.filter(id => id !== recordId) : [...prev, recordId]
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading onboarding records...</div>;
  }

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">Onboarding</h1>
          <p className="text-slate-600 mt-1">Customer onboarding with complete business details</p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <>
              {selectedRecords.length > 0 && (
                <>
                <Button variant="outline" onClick={() => handleExport(true)} disabled={exporting} className="gap-2">
                  {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                  Export Selected ({selectedRecords.length})
                </Button>
                <CsvDownloadButton onDownload={() => handleExport(true)} testId="onboarding-export-selected-csv" size="default" disabled={exporting} />
                </>
              )}
              <Button variant="outline" onClick={() => handleExport(false)} disabled={exporting} className="gap-2">
                {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                Export to Excel
              </Button>
              <CsvDownloadButton onDownload={() => handleExport(false)} testId="onboarding-export-csv" size="default" disabled={exporting} />
            </>
          )}
        {canCreateOnboarding('onboarding') && (
          <Button
            variant="outline"
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="bulk-upload-onboarding-btn"
            onClick={() => setBulkOpen(true)}
          >
            <Upload size={18} />
            Bulk Upload
          </Button>
        )}
        {canCreateOnboarding('onboarding') && (
          <Button className="gap-2" data-testid="add-onboarding-btn" onClick={openCreateDialog}>
            <Plus size={18} />
            Add Customer
          </Button>
        )}

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { resetForm(); setEditingId(null); setDialogMode('create'); }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>
                  {dialogMode === 'edit' && 'Edit Onboarding'}
                  {dialogMode === 'view' && 'View Onboarding'}
                  {dialogMode === 'create' && 'Customer Onboarding Form'}
                </DialogTitle>
                {/* Form layout toggle — user can switch between 2-column
                    (horizontal) and 1-column (vertical) layouts. */}
                <div className="flex gap-1 bg-slate-100 rounded-md p-1 mr-6" data-testid="form-layout-toggle">
                  <button
                    type="button"
                    onClick={() => setFormLayout('horizontal')}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${formLayout === 'horizontal' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="layout-horizontal-btn"
                    title="Horizontal (2 columns)"
                  >
                    <Columns2 size={14} /> Horizontal
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormLayout('vertical')}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${formLayout === 'vertical' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="layout-vertical-btn"
                    title="Vertical (1 column)"
                  >
                    <Rows size={14} /> Vertical
                  </button>
                </div>
              </div>
            </DialogHeader>
            <fieldset disabled={dialogMode === 'view'} style={{ border: 0, padding: 0, margin: 0 }}>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="onboarding-form">
              <div className={formLayout === 'vertical' ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-2 gap-4'}>
                <div className="space-y-2">
                  <Label htmlFor="name">Company/Business Name *</Label>
                  <Input
                    id="name"
                    data-testid="onboarding-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person *</Label>
                  <Input
                    id="contact_person"
                    data-testid="onboarding-contact-input"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile Number *</Label>
                  <Input
                    id="mobile"
                    data-testid="onboarding-mobile-input"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="onboarding-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="onboarding-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_segment">Business Segment Type *</Label>
                  <Select value={formData.business_segment} onValueChange={(value) => setFormData({ ...formData, business_segment: value })}>
                    <SelectTrigger data-testid="onboarding-segment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {businessSegments.map((segment) => (
                        <SelectItem key={segment} value={segment}>{segment}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    data-testid="onboarding-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                    <SelectTrigger data-testid="onboarding-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {indianStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    data-testid="onboarding-city-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Bangalore"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="district">District *</Label>
                  <Input
                    id="district"
                    data-testid="onboarding-district-input"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    data-testid="onboarding-pincode-input"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    placeholder="6-digit pincode"
                    maxLength={6}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input
                    id="gstin"
                    data-testid="onboarding-gstin-input"
                    value={formData.gstin}
                    onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                    placeholder="e.g., 27XXXXX1234X1ZX"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project_cost">Project Cost</Label>
                  <Input
                    id="project_cost"
                    data-testid="onboarding-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.project_cost}
                    onChange={(e) => setFormData({ ...formData, project_cost: e.target.value })}
                    placeholder="₹"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_store_size">Total Store Size (Sq Ft)</Label>
                  <Input
                    id="total_store_size"
                    data-testid="onboarding-size-input"
                    type="number"
                    step="0.01"
                    value={formData.total_store_size}
                    onChange={(e) => setFormData({ ...formData, total_store_size: e.target.value })}
                  />
                </div>

                {/* Document Uploads */}
                <div className="col-span-2 border-t pt-4 mt-4">
                  <h3 className="font-semibold text-primary mb-3">Document Uploads</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>GST Document</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, 'gst_document')}
                        className="hidden"
                        id="gst-doc-upload"
                      />
                      <Label htmlFor="gst-doc-upload" className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-md hover:bg-slate-50">
                          <Upload size={18} />
                          <span className="text-sm">{formData.gst_document ? 'Uploaded' : 'Upload GST'}</span>
                        </div>
                      </Label>
                      {formData.gst_document && (
                        <a href={`${BACKEND_URL}${formData.gst_document}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <FileText size={12} /> View
                        </a>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Aadhaar Document</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, 'aadhaar_document')}
                        className="hidden"
                        id="aadhaar-upload"
                      />
                      <Label htmlFor="aadhaar-upload" className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-md hover:bg-slate-50">
                          <Upload size={18} />
                          <span className="text-sm">{formData.aadhaar_document ? 'Uploaded' : 'Upload Aadhaar'}</span>
                        </div>
                      </Label>
                      {formData.aadhaar_document && (
                        <a href={`${BACKEND_URL}${formData.aadhaar_document}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <FileText size={12} /> View
                        </a>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Store Image</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'store_image')}
                        className="hidden"
                        id="store-image-upload"
                      />
                      <Label htmlFor="store-image-upload" className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-md hover:bg-slate-50">
                          <Upload size={18} />
                          <span className="text-sm">{formData.store_image ? 'Uploaded' : 'Upload Image'}</span>
                        </div>
                      </Label>
                      {formData.store_image && (
                        <a href={`${BACKEND_URL}${formData.store_image}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <FileText size={12} /> View
                        </a>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Agreement</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, 'agreement_document')}
                        className="hidden"
                        id="agreement-upload"
                        data-testid="agreement-upload-input"
                      />
                      <Label htmlFor="agreement-upload" className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-md hover:bg-slate-50 border-emerald-300 bg-emerald-50/30">
                          <Upload size={18} className="text-emerald-600" />
                          <span className="text-sm">{formData.agreement_document ? 'Uploaded' : 'Upload Agreement'}</span>
                        </div>
                      </Label>
                      {formData.agreement_document && (
                        <div className="flex items-center gap-3">
                          <a href={`${BACKEND_URL}${formData.agreement_document}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                            <FileText size={12} /> View
                          </a>
                          <a href={`${BACKEND_URL}${formData.agreement_document}`} download className="text-xs text-green-600 hover:underline flex items-center gap-1">
                            <Download size={12} /> Download
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="onboarding-cancel-btn">
                  {dialogMode === 'view' ? 'Close' : 'Cancel'}
                </Button>
                {dialogMode !== 'view' && (
                  <Button type="submit" data-testid="onboarding-submit-btn">
                    {dialogMode === 'edit' ? 'Update Onboarding' : 'Create Onboarding'}
                  </Button>
                )}
              </div>
            </form>
            </fieldset>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <Input
              data-testid="onboarding-search-input"
              placeholder="Search by name, email, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Segment summary cards — click a tile to filter the list by segment. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="segment-summary-cards">
        <button
          type="button"
          onClick={() => setSegmentFilter(null)}
          className={`text-left p-3 rounded-lg border transition-all ${!segmentFilter ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
          data-testid="segment-card-all"
        >
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-md bg-slate-100 text-slate-700">
              <Users size={16} />
            </div>
            <div>
              <p className="text-xs text-slate-500">All</p>
              <p className="text-xl font-bold text-slate-900">{onboardingList.length}</p>
            </div>
          </div>
        </button>
        {SEGMENT_CARDS.map((seg) => {
          const Icon = seg.icon;
          const count = segmentCounts[seg.key] || 0;
          const active = segmentFilter === seg.key;
          return (
            <button
              type="button"
              key={seg.key}
              onClick={() => setSegmentFilter(active ? null : seg.key)}
              className={`text-left p-3 rounded-lg border transition-all ${active ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
              data-testid={`segment-card-${seg.key.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-md ${seg.bg} ${seg.color}`}>
                  <Icon size={16} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 truncate">{seg.label}</p>
                  <p className={`text-xl font-bold ${seg.color}`}>{count}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {segmentFilter && (
        <div className="text-xs text-slate-600 flex items-center gap-2">
          Showing <strong>{segmentFilter}</strong> only —
          <button
            type="button"
            onClick={() => setSegmentFilter(null)}
            className="text-blue-600 hover:underline"
            data-testid="clear-segment-filter"
          >
            clear filter
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOnboarding.map((record) => (
          <Card key={record.id} className="border-slate-200 shadow-sm hover:border-accent/50 transition-colors" data-testid={`onboarding-card-${record.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading font-bold text-lg text-primary">{record.name}</h3>
                  <p className="font-mono text-xs text-accent font-semibold">{record.franchise_id}</p>
                </div>
                <Badge variant="secondary">{record.business_segment}</Badge>
              </div>
              <div className="space-y-1 text-sm text-slate-600">
                <p><strong>Contact:</strong> {record.contact_person}</p>
                <p><strong>Mobile:</strong> {record.mobile}</p>
                <p><strong>Email:</strong> {record.email}</p>
                <p><strong>Location:</strong> {[record.city, record.district, record.state].filter(Boolean).join(', ')}{record.pincode ? ` - ${record.pincode}` : ''}</p>
                {record.gstin && <p className="font-mono text-xs"><strong>GSTIN:</strong> {record.gstin}</p>}
                {record.project_cost && (
                  <p className="font-mono"><strong>Project Cost:</strong> ₹{record.project_cost.toLocaleString()}</p>
                )}
                {record.total_store_size && (
                  <p><strong>Store Size:</strong> {record.total_store_size} sq ft</p>
                )}
                {(record.gst_document || record.aadhaar_document || record.store_image) && (
                  <div className="flex gap-2 pt-2">
                    {record.gst_document && <Badge variant="outline"><FileText size={12} className="mr-1" />GST</Badge>}
                    {record.aadhaar_document && <Badge variant="outline"><FileText size={12} className="mr-1" />Aadhaar</Badge>}
                    {record.store_image && <Badge variant="outline"><FileText size={12} className="mr-1" />Image</Badge>}
                  </div>
                )}
              </div>

              {/* Row actions — View / Edit / Delete */}
              <div className="flex gap-2 pt-3 mt-3 border-t border-slate-100">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 gap-1"
                  onClick={() => openViewDialog(record)}
                  data-testid={`view-onboarding-${record.id}`}
                >
                  <Eye size={14} /> View
                </Button>
                {canEditOnboarding('onboarding') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1"
                    onClick={() => openEditDialog(record)}
                    data-testid={`edit-onboarding-${record.id}`}
                  >
                    <Pencil size={14} /> Edit
                  </Button>
                )}
                {canDeleteOnboarding('onboarding') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={() => setDeletingRecord(record)}
                    data-testid={`delete-onboarding-${record.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deletingRecord} onOpenChange={(o) => !o && setDeletingRecord(null)}>
        <DialogContent className="max-w-md" data-testid="delete-onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Onboarding Record?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>You are about to delete <strong>{deletingRecord?.name}</strong> ({deletingRecord?.franchise_id}).</p>
            <p className="text-slate-600">
              This action is permanent. Records with linked orders or invoices can not be deleted — you'll get an error instead.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setDeletingRecord(null)} data-testid="delete-onboarding-cancel">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-onboarding-confirm"
            >
              {deleting ? 'Deleting…' : 'Yes, Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {filteredOnboarding.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="text-center py-12 text-slate-500" data-testid="no-onboarding-message">
            No onboarding records found. Add your first customer to get started.
          </CardContent>
        </Card>
      )}

      <OnboardingBulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onComplete={fetchData}
      />
    </div>
  );
};

export default Onboarding;
